import axios from 'axios';
import store from '../../store/store'; // Import Redux Store
import { mapApiUtcToPolishTime, mapPolishTimeToApiUtc } from '../../utils/dateTime';

const baseURL = ''; // Podstawowy URL API
// const baseURL = 'https://api-dev.mapenapp.com/'; // Podstawowy URL API

const ApiService = axios.create({  
  baseURL: baseURL, // Podstawowy URL API
  timeout: 5000, // Limit czasu na odpowiedź (5 sekund)
});

const toTimestampMs = (value) => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

ApiService.logoutAndRedirectToLogin = (message = 'Sesja wygasła. Zaloguj się ponownie.') => {
  store.dispatch({ type: 'tokens/clearTokens' });
  alert(message);
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Interceptor żądań (np. dodawanie tokenów autoryzacyjnych)
ApiService.interceptors.request.use(
  async (config) => {
    // Wszystkie daty-czasy w ciele i query stringu opuszczają panel jako UTC.
    // Daty bez godziny pozostają datami kalendarzowymi (np. filtry dateFrom/dateTo).
    config.data = mapPolishTimeToApiUtc(config.data);
    config.params = mapPolishTimeToApiUtc(config.params);

    const requestUrl = String(config?.url || '');
    const isAuthEndpoint =
      requestUrl.includes('/api/auth/login') ||
      requestUrl.includes('/api/auth/access-token') ||
      requestUrl.includes('/api/password-recovery');
    const state = store.getState();
    const hasAnyToken = Boolean(state?.tokens?.accessToken || state?.tokens?.refreshToken);

    if (!isAuthEndpoint && hasAnyToken) {
      const isValid = await ApiService.checkTokenExpiration();
      if (!isValid) {
        return Promise.reject(new Error('Session expired'));
      }

      const token = store.getState().tokens.accessToken;
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    console.error('Błąd w żądaniu:', error);
    return Promise.reject(error);
  }
);

ApiService.checkTokenExpiration = async () => {
  const state = store.getState();
  const { accessToken, refreshToken, accessTokenExpiration, refreshTokenExpiration } = state.tokens;

  const currentTime = Date.now();
  const accessTokenExpiresAtMs = toTimestampMs(accessTokenExpiration);
  const refreshTokenExpiresAtMs = toTimestampMs(refreshTokenExpiration);

  if (!accessToken || !accessTokenExpiresAtMs) {
    ApiService.logoutAndRedirectToLogin();
    return false;
  }

  if (accessTokenExpiresAtMs <= currentTime) {
    console.warn('Access token wygasł.');

    if (refreshToken && refreshTokenExpiresAtMs && refreshTokenExpiresAtMs > currentTime) {
      console.log('Odświeżanie tokenu...');
      try {
        await ApiService.refreshTokens(refreshToken);
        return true; // Tokeny zostały odświeżone
      } catch (error) {
        console.error('Błąd podczas odświeżania tokenu:', error);
        ApiService.logoutAndRedirectToLogin();
        return false;
      }
    } else {
      console.warn('Refresh token również wygasł. Wylogowywanie użytkownika...');
      ApiService.logoutAndRedirectToLogin();
      return false;
    }
  }

  console.log('Tokeny są ważne.');
  return true; // Tokeny są ważne
};

ApiService.refreshTokens = async (refreshToken) => {
  try {
    const response = await ApiService.get('/api/auth/access-token', {
      headers: {
        Authorization: `Bearer ${refreshToken}`, // Dodanie refreshToken do nagłówka
      },
    });

    const { token: accessToken, expiresAt: accessTokenExpiresAt } = response.data;

    // Zaktualizuj stan Redux
    store.dispatch({
      type: 'tokens/setAccessTokenWithExpiration',
      payload: {
        accessToken,        
        accessTokenExpiration: accessTokenExpiresAt,
      },
    });

    console.log('Token został odświeżony.');
    return { accessToken, accessTokenExpiresAt };
  } catch (error) {
    console.error('Błąd podczas odświeżania tokenu:', error);
    throw error;
  }
};

// Interceptor żądań (dodawanie tokenów autoryzacyjnych)
ApiService.getHeadersJsonUTF8OrDifferent = (contentType = 'application/json; charset=UTF-8') => {
  return {
    'Content-Type': contentType,
  };
};
ApiService.getHeaderOnlyToken = () => {
  return {};
};


// Interceptor odpowiedzi (obsługa błędów)
ApiService.interceptors.response.use(
  (response) => {
    // Kontrakt API: każda data-czas w odpowiedzi jest UTC. Widoki dostają
    // odpowiadający jej lokalny czas Polski (CET/CEST).
    response.data = mapApiUtcToPolishTime(response.data);
    return response;
  },
  (error) => {
    if (error?.code === 'ERR_CANCELED' || axios.isCancel?.(error)) {
      return Promise.reject(error);
    }

    if (error.response) {
      const { status, data } = error.response;
      const requestUrl = String(error.config?.url || '');
      const isBypassAlertRequest =
        error.config?.silent ||
        error.config?.bypassAlert ||
        requestUrl.includes('/api/auth/login') ||
        requestUrl.includes('/api/password-recovery') ||
        requestUrl.includes('/api/favorite-events');

      if (isBypassAlertRequest) {
        return Promise.reject(error);
      }

      switch (status) {
        case 400:
          console.error('Błąd 400: Nieprawidłowe żądanie', data);
          alert('Nieprawidłowe dane.');
          break;
        case 401:
          console.error('Błąd 401: Nieautoryzowany', data);
          ApiService.logoutAndRedirectToLogin();
          break;
        case 403:
          console.error('Błąd 403: Brak dostępu', data);
          alert('Nie masz uprawnień do wykonania tej operacji.');
          break;
        case 404:
          console.error('Błąd 404: Nie znaleziono zasobu', data);
          alert('Nie znaleziono zasobu.');
          break;
        case 500:
          console.error('Błąd 500: Błąd serwera', data);
          alert('Wystąpił błąd serwera. Spróbuj ponownie później.');
          break;
        default:
          console.error(`Błąd ${status}:`, data);
          alert('Wystąpił nieoczekiwany błąd. Spróbuj ponownie.');
      }
    } else if (error.request) {
      console.error('Brak odpowiedzi z serwera:', error.request);
      alert('Nie można połączyć się z serwerem. Sprawdź swoje połączenie internetowe.');
    } else {
      console.error('Błąd:', error.message);
      alert('Wystąpił błąd. Spróbuj ponownie.');
    }
    return Promise.reject(error);
  }
);

///-------------------------------------------------------------------------
// Funkcja logowania
ApiService.login = async (login, pass) => {
  try {
    const response = await ApiService.post('/api/auth/login/superadmin', {
      email: login,
      password: pass,
    });
    return response.data; // Zwróć dane odpowiedzi (np. tokeny)
  } catch (error) {
    throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
  }
};
///-------------------------------------------------------------------------
// events

ApiService.getEvents = async (sortBy, sortDirection, pageNumber, searchQuery, filterQuery = null, pageSize = null) => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
    await ApiService.checkTokenExpiration();

    try {
        const payload = {
            ...(sortBy !== null && sortDirection !== null && { sortBy }),
            ...(sortBy !== null && sortDirection !== null && { sortDirection }),
            ...(searchQuery !== null && { searchQuery }),
            ...(filterQuery !== null && { filterQuery }),
            ...(pageSize !== null && { pageSize }),
            pageNumber
        }
        const response = await ApiService.get('/api/events/paginated-sorted-and-with-search', {
            headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
            params: payload,
        });
        return response.data; // Zwróć dane użytkowników
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

ApiService.getEvents2 = async () => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/events/only-not-approved', {
            headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
        });
        return response // Zwróć dane użytkowników
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

ApiService.getEventsTopCharts = async (path) => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/events/top/' + path, {
            headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
        });
        return response // Zwróć dane użytkowników
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

ApiService.getEventsTop = async (timeScope, categoryId, sortCountingScope) => {
    await ApiService.checkTokenExpiration();

    const safeTimeScope = timeScope ?? 'ALL_TIME';
    const safeCategoryId = categoryId ?? 0;
    const safeSortScope = sortCountingScope ?? 'VIEWING';

    try {
        const response = await ApiService.get(
            `/api/events/top/${safeTimeScope}/${safeCategoryId}/${safeSortScope}`,
            {
                headers: ApiService.getHeaderOnlyToken(),
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getEvent = async (eventId) => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/events/' + eventId, {
            headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
            params: { source: 'PANEL' }, // Ruch z panelu React — pomijany w statystykach oglądalności
        });
        return response // Zwróć dane użytkowników
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

ApiService.getAllImagesForEvent = async (eventId) => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/events-images/by-event-id/' + eventId, {
            headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
        });
        return response // Zwróć dane użytkowników
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

ApiService.getEventsCategories = async () => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/events-categories', {
            headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
        });
        return response // Zwróć dane użytkowników
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

ApiService.updateEvent = async (id, data) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.put('/api/events/'+ id, data, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
            params: { scope: 'SA' },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.patchEvent = async (id, data) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch('/api/events/' + id, data, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
            params: { scope: 'SA' },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.approveEvent = async (id) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch('/api/events/approve/' + id, null, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.rejectEvent = async (payload) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch('/api/events/reject', payload, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getPendingEventEdits = async () => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/events/pending-edits', {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.approvePendingEventEdit = async (id) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch(`/api/events/pending-edits/approve/${id}`, null, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.rejectPendingEventEdit = async (payload) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch('/api/events/pending-edits/reject', payload, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.deleteEvent = async (id) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.delete('/api/events/' + id, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.deleteEventImage = async (id) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.delete('/api/events-images/'+ id, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
            params: { scope: 'SA' },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.addNewEvent = async (data) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.post('/api/events', data, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.uploadEventImage = async (eventId, file) => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne
    await ApiService.checkTokenExpiration();

    const formData = new FormData();
    formData.append('file', file); // Dodanie pliku do formData

    try {
        const response = await ApiService.post('/api/events-images/image-upload/' + eventId, formData, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent('multipart/form-data'),
            params: { scope: 'SA' },
        });
        return response.data; // Zwróć ciąg znaków zwrócony przez endpoint
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

///-------------------------------------------------------------------------
// bunch-of-friends
ApiService.getBunchesByEventId = async (eventId) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/bunch-of-friends/by-event-id/${eventId}`, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getBunchesCreatedByUser = async (userId, options = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/bunch-of-friends/created-by-user/${userId}`, {
            headers: ApiService.getHeaderOnlyToken(),
            ...options,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getProfileStats = async (userId, options = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/profiles/stats/${encodeURIComponent(userId)}`, {
            headers: ApiService.getHeaderOnlyToken(),
            ...options,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getUserProfileStats = ApiService.getProfileStats;

///-------------------------------------------------------------------------
// favorite events
ApiService.getFavoriteEventsCountByUserId = async (userId, options = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/favorite-events/count/${encodeURIComponent(userId)}`, {
            headers: ApiService.getHeaderOnlyToken(),
            ...options,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getUserFavoriteEventsCount = ApiService.getFavoriteEventsCountByUserId;

ApiService.getCurrentUserFavoriteEventsCount = async (options = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/favorite-events/count', {
            headers: ApiService.getHeaderOnlyToken(),
            ...options,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getFavoriteEventsCount = ApiService.getCurrentUserFavoriteEventsCount;

ApiService.getFavoriteEvents = async (options = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/favorite-events', {
            headers: ApiService.getHeaderOnlyToken(),
            ...options,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

///-------------------------------------------------------------------------
// statistics
ApiService.getStatisticsSummary = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/statistics/summary', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsDemographics = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/statistics/demographics', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsTopCities = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/statistics/top-cities', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        if (error?.response?.status !== 404) {
            throw error;
        }

        try {
            const fallbackResponse = await ApiService.get('/api/v1/statistics/cities/top', {
                headers: ApiService.getHeaderOnlyToken(),
                params,
            });
            return fallbackResponse.data;
        } catch (fallbackError) {
            throw fallbackError;
        }
    }
};

ApiService.getStatisticsSeries = async (metricType, params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/v1/statistics/series/${metricType}`, {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsB2BSummary = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/statistics/b2b/summary', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsB2BSeries = async (metricType, params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/v1/statistics/b2b/series/${metricType}`, {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsEventsSummary = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/statistics/events/summary', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsEventsAverageParticipantsSeries = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/statistics/events/series/average-participants', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsEventsCategories = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/statistics/events/categories', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsEventsRanking = async (metricType, params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/v1/statistics/events/ranking/${metricType}`, {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsRankingsEvents = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/statistics/rankings/events', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.searchEventsForStatistics = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/events/search', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Wyszukiwanie wydarzeń dostępne dla administratora (nazwa, organizator,
// miejsce, miasto, opis i tagi są obsługiwane po stronie API).
ApiService.searchEventsForAdmin = async (searchQuery = '', options = {}) => {
    await ApiService.checkTokenExpiration();

    const search = String(searchQuery || '').trim();

    try {
        const response = await ApiService.get('/api/events/admin/search', {
            headers: ApiService.getHeaderOnlyToken(),
            params: search ? { search } : undefined,
            ...options,
        });
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        throw error;
    }
};

ApiService.searchEventsForAdminV2 = async (searchQuery = '', options = {}) => {
    await ApiService.checkTokenExpiration();

    const search = String(searchQuery || '').trim();

    try {
        const response = await ApiService.get('/api/events/admin/search-v2', {
            headers: ApiService.getHeaderOnlyToken(),
            params: search ? { search } : undefined,
            ...options,
        });
        const responseData = response?.data;
        if (Array.isArray(responseData)) {
            return responseData;
        }
        if (Array.isArray(responseData?.data)) {
            return responseData.data;
        }
        if (Array.isArray(responseData?.content)) {
            return responseData.content;
        }
        if (Array.isArray(responseData?.items)) {
            return responseData.items;
        }
        return [];
    } catch (error) {
        throw error;
    }
};

ApiService.getEventsSimpleList = async (params = {}, options = {}) => {
    await ApiService.checkTokenExpiration();

    const { organizerIds, ...restParams } = params || {};
    const normalizedParams = {
        ...restParams,
        ...(Array.isArray(organizerIds) && organizerIds.length > 0
            ? { organizerIds: organizerIds.join(',') }
            : {}),
    };

    try {
        const response = await ApiService.get('/api/events/simple-list', {
            headers: ApiService.getHeaderOnlyToken(),
            params: normalizedParams,
            ...options,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.searchEventOrganizerIds = async (search, options = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/events/organizers/search-ids', {
            headers: ApiService.getHeaderOnlyToken(),
            params: { search },
            ...options,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsEventSummary = async (eventId, params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/v1/statistics/events/${eventId}/summary`, {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsEventDemographicsAge = async (eventId, params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/v1/statistics/events/${eventId}/demographics/age`, {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsEventActivitySeries = async (eventId, params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/v1/statistics/events/${eventId}/series/activity`, {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getEventStatisticsViewing = async (eventId = null) => {
    await ApiService.checkTokenExpiration();

    const path = eventId ? `/api/event-statistics/viewing/${eventId}` : '/api/event-statistics/viewing';

    try {
        const response = await ApiService.get(path, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getBunchActivityChart = async (fromUtc, toUtc, eventId = null) => {
    await ApiService.checkTokenExpiration();

    const encodedFrom = encodeURIComponent(fromUtc);
    const encodedTo = encodeURIComponent(toUtc);
    const path = eventId
        ? `/api/bunch-statistics/activity-chart/${encodedFrom}/${encodedTo}/${eventId}`
        : `/api/bunch-statistics/activity-chart/${encodedFrom}/${encodedTo}`;

    try {
        const response = await ApiService.get(path, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

///-------------------------------------------------------------------------
// password
ApiService.changePasswordWithOld = async (oldPassword, newPassword) => {
  await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch(
            '/api/password-changing/with-old-password',
            { oldPassword, newPassword },
            { headers: ApiService.getHeadersJsonUTF8OrDifferent() }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

///-------------------------------------------------------------------------
// system admin profiles
ApiService.getSystemAdminProfiles = async (params = {}, options = {}) => {
  await ApiService.checkTokenExpiration();

  const normalizedParams = {
    pageNumber: 1,
    pageSize: 25,
    roleScope: 'ALL_USERS',
    blockedOnly: false,
    ...params,
  };

  try {
    const response = await ApiService.get('/api/system_admin/profiles', {
      headers: ApiService.getHeaderOnlyToken(),
      params: normalizedParams,
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

///-------------------------------------------------------------------------
// password recovery (public endpoint)
ApiService.requestPasswordRecovery = async (email) => {
  try {
    const response = await ApiService.get(`/api/password-recovery/${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

///-------------------------------------------------------------------------
// organizer registration (admin-assisted flow)
ApiService.registerOrganiser = async (payload) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.post('/api/accounts/register-organiser', payload, {
      headers: ApiService.getHeadersJsonUTF8OrDifferent(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getOrganiserProfiles = async (params = {}, options = {}) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get('/api/organiser/profiles', {
      headers: ApiService.getHeaderOnlyToken(),
      params,
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getOrganiserProfilesPaginated = async (params = {}, options = {}) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get('/api/organiser/profiles/paginated', {
      headers: ApiService.getHeaderOnlyToken(),
      params,
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getOrganiserProfile = async (id, options = {}) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get(`/api/organiser/profiles/${id}`, {
      headers: ApiService.getHeaderOnlyToken(),
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.countEventsByOrganiserId = async (organiserId, options = {}) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get(`/api/events/count-events-by-organiser-id/${encodeURIComponent(organiserId)}`, {
      headers: ApiService.getHeaderOnlyToken(),
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.updateOrganiserProfile = async (payload) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.patch('/api/organiser/profiles', payload, {
      headers: ApiService.getHeadersJsonUTF8OrDifferent(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.approveOrganiser = async (id) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.patch(`/api/organiser/approve/${id}`, null, {
      headers: ApiService.getHeaderOnlyToken(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.rejectOrganiser = async (payload) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.patch('/api/organiser/reject', payload, {
      headers: ApiService.getHeadersJsonUTF8OrDifferent(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getOnlyNotApprovedOrganisers = async (options = {}) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get('/api/organiser/only-not-approved', {
      headers: ApiService.getHeaderOnlyToken(),
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getPendingOrganiserChanges = async (options = {}) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get('/api/organiser/pending-changes', {
      headers: ApiService.getHeaderOnlyToken(),
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.approvePendingOrganiserChange = async (id) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.patch(`/api/organiser/pending-changes/approve/${id}`, null, {
      headers: ApiService.getHeaderOnlyToken(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.rejectPendingOrganiserChange = async (payload) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.patch('/api/organiser/pending-changes/reject', payload, {
      headers: ApiService.getHeadersJsonUTF8OrDifferent(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getUserReports = async (options = {}) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get('/api/user/report', {
      headers: ApiService.getHeaderOnlyToken(),
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getUserReportsByReportedUser = async (userId, options = {}) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get(`/api/user/report/reported-user/${userId}`, {
      headers: ApiService.getHeaderOnlyToken(),
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getBunchMessageReports = async (bunchId, options = {}) => {
  await ApiService.checkTokenExpiration();
  const response = await ApiService.get(`/api/bunch-of-friends/message/report/${bunchId}`, { headers: ApiService.getHeaderOnlyToken(), ...options });
  return response.data;
};

ApiService.getAllMessageReports = async (options = {}) => {
  await ApiService.checkTokenExpiration();
  const response = await ApiService.get('/api/bunch-of-friends/message/report', {
    headers: ApiService.getHeaderOnlyToken(), ...options,
  });
  return response.data;
};

ApiService.getMessageReports = async (params = {}, options = {}) => {
  await ApiService.checkTokenExpiration();
  const response = await ApiService.get('/api/bunch-of-friends/message/reports', {
    params: {
      groupBy: 'REPORT',
      includeResolved: false,
      page: 0,
      size: 50,
      sort: 'lastReportedAt,desc',
      ...params,
    },
    headers: ApiService.getHeaderOnlyToken(),
    ...options,
  });
  return response.data;
};

ApiService.resolveMessageReports = async (reportIds, options = {}) => {
  await ApiService.checkTokenExpiration();
  const response = await ApiService.post('/api/bunch-of-friends/message/report/resolve', reportIds, {
    headers: ApiService.getHeadersJsonUTF8OrDifferent(), ...options,
  });
  return response.data;
};

ApiService.resolveMessageReportGroup = async (reportGroupId, options = {}) => {
  await ApiService.checkTokenExpiration();
  const response = await ApiService.post('/api/bunch-of-friends/message/reports/resolve-group', { reportGroupId }, {
    headers: ApiService.getHeadersJsonUTF8OrDifferent(), ...options,
  });
  return response.data;
};

ApiService.resolveUserReports = async (reportIds, options = {}) => {
  await ApiService.checkTokenExpiration();
  const response = await ApiService.post('/api/user/report/resolve', reportIds, {
    headers: ApiService.getHeadersJsonUTF8OrDifferent(), ...options,
  });
  return response.data;
};

ApiService.resolveBunchMessageReports = async (bunchId, reportIds, options = {}) => {
  await ApiService.checkTokenExpiration();
  const response = await ApiService.post(`/api/bunch-of-friends/message/report/resolve/${bunchId}`, reportIds, { headers: ApiService.getHeadersJsonUTF8OrDifferent(), ...options });
  return response.data;
};

ApiService.getProfileById = async (id, options = {}) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get(`/api/profiles/${id}`, {
      headers: ApiService.getHeaderOnlyToken(),
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.putWarning = async (payload) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.put('/api/warning', payload, {
      headers: ApiService.getHeadersJsonUTF8OrDifferent(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.putBan = async (payload) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.put('/api/ban', payload, {
      headers: ApiService.getHeadersJsonUTF8OrDifferent(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.unblockUser = async (id) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.put(`/api/unban/${encodeURIComponent(id)}`, undefined, {
      headers: ApiService.getHeaderOnlyToken(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.checkUserIsParticipant = async (bunchId, userId) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get(
      `/api/admin/bunch-of-friends/${encodeURIComponent(bunchId)}/is-participant/${encodeURIComponent(userId)}`,
      {
        headers: ApiService.getHeaderOnlyToken(),
      }
    );
    return response.data === true;
  } catch (error) {
    throw error;
  }
};

export default ApiService;
